import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

/**
 * Privacy Policy — minimum-viable baseline. Covers what we collect (call
 * recordings, transcripts, lead PII), why we collect it, how long we keep
 * it (90 days default for recordings), who we share with (Vapi, Twilio,
 * Stripe, OpenAI/11Labs/Deepgram as sub-processors), and the standard
 * GDPR/CCPA rights. Replace with a generated version before scaling past
 * 100 customers or hitting regulated verticals.
 */
export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#fafaf7] text-[#0f172a]">
      <header className="border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-lg font-heading font-medium tracking-tight">Nextbell.ai</Link>
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-900">← Back home</Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16 prose prose-slate">
        <p className="text-xs uppercase tracking-[3px] text-gray-400 font-mono">Last updated: June 19, 2026</p>
        <h1 className="font-heading text-4xl md:text-5xl mt-2 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">How Nextbell collects, uses, stores, and shares your data — written in plain English.</p>

        <Section title="1. The short version">
          <ul>
            <li>We collect what we need to run the service. Nothing more.</li>
            <li>We never sell your data. Period.</li>
            <li>We never use your business&apos; customer calls to train AI models that serve other businesses.</li>
            <li>Call recordings are kept for 90 days then auto-deleted.</li>
            <li>You can export or delete your data anytime from Settings.</li>
          </ul>
        </Section>

        <Section title="2. Who this applies to">
          <p>Three groups of people are covered:</p>
          <ul>
            <li><strong>Customers</strong> — the small-business owners who sign up for a Nextbell account.</li>
            <li><strong>Callers</strong> — the end-users (typically the Customer&apos;s customers) who phone the AI receptionist.</li>
            <li><strong>Visitors</strong> — anyone browsing nextbell.ai.</li>
          </ul>
        </Section>

        <Section title="3. What we collect from Customers">
          <ul>
            <li>Account info: name, email, password (hashed), phone number, business name + address, niche, role.</li>
            <li>Billing info: handled and stored entirely by Stripe — we only see the last four digits, brand, and expiration of your card. This applies to both subscription payments and Pay&nbsp;As&nbsp;You&nbsp;Go credit top-ups; we store only your credit balance and a transaction history, never card numbers.</li>
            <li>Usage data: which features you use, login timestamps, IP address, browser/device fingerprint (for fraud prevention).</li>
            <li>Optional: a Cal.com API key, if you connect a calendar so Ava can book appointments onto it. Stored securely and used only to create bookings on your behalf; remove it anytime from Settings → Calendar.</li>
          </ul>
        </Section>

        <Section title="4. What we collect from Callers">
          <ul>
            <li>Phone number (caller ID).</li>
            <li>Voice recording of the call.</li>
            <li>Text transcript of the call.</li>
            <li>Any information the caller provides to the AI (name, email, appointment details, free-form description of why they called).</li>
          </ul>
          <p>Callers are explicitly informed at the start of every call that their call may be recorded for quality assurance and training. They may opt out by hanging up.</p>
        </Section>

        <Section title="5. What we collect from Visitors">
          <ul>
            <li>Standard web-analytics data: page views, referrer, country (from IP), device type. We use a privacy-respecting analytics provider that does not set advertising cookies.</li>
            <li>If you start a demo call with Ava on the homepage: the audio of that demo for the duration of the call only. Demo audio is deleted within 24 hours.</li>
          </ul>
        </Section>

        <Section title="6. How we use it">
          <ul>
            <li>To run the AI receptionist service (mandatory).</li>
            <li>To bill you (mandatory if you&apos;re a paid customer).</li>
            <li>To send service emails (welcome, password reset, billing receipts, deletion notices).</li>
            <li>To debug bugs and prevent fraud or abuse.</li>
            <li>To improve our platform — but only using aggregated, de-identified data.</li>
            <li>To send marketing emails — only if you opt in. You can opt out anytime via the unsubscribe link in every email.</li>
          </ul>
        </Section>

        <Section title="7. Who we share with (sub-processors)">
          <p>We use a small number of vetted third-party services to deliver our product. Each has signed a data-processing agreement with us.</p>
          <ul>
            <li><strong>Vapi.ai</strong> — voice AI orchestration. Processes call audio + transcripts in real time.</li>
            <li><strong>Twilio</strong> — telephony and SMS delivery. Processes phone numbers + SMS message content.</li>
            <li><strong>OpenAI</strong> — LLM that powers Ava&apos;s conversation. Receives the active call transcript only. OpenAI does not retain or train on this data per our enterprise agreement.</li>
            <li><strong>11Labs</strong> — voice synthesis for Ava&apos;s spoken responses.</li>
            <li><strong>Deepgram</strong> — speech-to-text transcription.</li>
            <li><strong>Stripe</strong> — payment processing for subscriptions and Pay&nbsp;As&nbsp;You&nbsp;Go credits. Stores card details on PCI-compliant infrastructure; we never see them.</li>
            <li><strong>Cal.com</strong> — optional calendar booking. If you connect it, the appointment details Ava books (name, time, and the contact details the caller provided) are sent to your Cal.com account to create the booking.</li>
            <li><strong>Hostinger</strong> — server hosting + outbound SMTP.</li>
            <li><strong>MongoDB Atlas</strong> — primary database. Encrypted at rest, in transit, and in backup.</li>
            <li><strong>Sentry</strong> (if enabled) — error tracking. PII is scrubbed before transmission.</li>
          </ul>
          <p>We do not sell, rent, or trade your data to any third party for marketing or advertising purposes.</p>
          <p>This list may change as our product evolves. We will keep this page current and notify active customers of any material addition of a sub-processor, giving you a reasonable opportunity to object.</p>
        </Section>

        <Section title="8. Retention">
          <ul>
            <li>Call recordings + transcripts: 90 days, then auto-deleted.</li>
            <li>Lead + appointment data: retained as long as your account is active. After you delete your account it is fully restorable for a 25-day grace window, then permanently deleted — so restoring within that window means no data loss.</li>
            <li>Billing records: retained 7 years for tax and accounting compliance.</li>
            <li>Server logs: 30 days.</li>
            <li>Marketing analytics (de-identified): 24 months.</li>
          </ul>
        </Section>

        <Section title="9. Your rights">
          <p>Regardless of where you live, you have the right to:</p>
          <ul>
            <li>Access — request a copy of the personal data we have about you.</li>
            <li>Correct — fix anything that&apos;s wrong.</li>
            <li>Delete — close your account and request erasure of your data (subject to legal retention requirements like tax records).</li>
            <li>Export — download your leads, calls, transcripts, and contacts in standard CSV format.</li>
            <li>Object / Restrict — tell us to stop using your data for non-essential purposes.</li>
          </ul>
          <p>Email <a href="mailto:privacy@nextbell.ai">privacy@nextbell.ai</a> from the address on your account to exercise any of these. We respond within 30 days.</p>
        </Section>

        <Section title="10. Region-specific">
          <ul>
            <li><strong>GDPR (EU/UK):</strong> Our legal basis is contract (running the service you signed up for) and legitimate interest (fraud prevention, product improvement). You may lodge a complaint with your local Data Protection Authority.</li>
            <li><strong>CCPA / CPRA (California):</strong> California residents have the right to know, delete, correct, and opt out of any &ldquo;sale&rdquo; or &ldquo;share&rdquo; of personal information. We do not sell or share personal information.</li>
            <li><strong>PIPEDA (Canada):</strong> Same access/correction/withdrawal rights — Canada is also where our company is established.</li>
          </ul>
        </Section>

        <Section title="11. Security">
          <p>All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Passwords are hashed with bcrypt. Internal access to customer data is limited to a small number of engineers, logged, and reviewed quarterly. We do not have a SOC 2 report yet — that&apos;s on our roadmap.</p>
        </Section>

        <Section title="12. Cookies">
          <p>We use first-party cookies for authentication only (the <code>access_token</code> session cookie). No advertising or tracking cookies. Our analytics provider is cookie-less.</p>
        </Section>

        <Section title="13. Children">
          <p>Nextbell is not intended for users under 18. We do not knowingly collect data from anyone under 18. If we learn we have, we will delete it.</p>
        </Section>

        <Section title="14. Changes to this policy">
          <p>We will email all customers at least 14 days before any material change. For non-material changes (typo fixes, clarifications) the &ldquo;Last updated&rdquo; date at the top will reflect the change.</p>
        </Section>

        <Section title="15. Contact">
          <p>Privacy questions: <a href="mailto:privacy@nextbell.ai">privacy@nextbell.ai</a> · General support: <a href="mailto:support@nextbell.ai">support@nextbell.ai</a></p>
        </Section>
      </article>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-heading text-xl font-medium mt-8 mb-3 text-gray-900">{title}</h2>
      <div className="text-[15px] leading-relaxed text-gray-700 space-y-3">{children}</div>
    </section>
  );
}
